'use client'

import Loader from '@/components/common/loader'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useMemo } from 'react'
import StandingsTeamTableRow from './standings-team-table-row'

export default function StandingsTeamTable({
  championship,
  isLoading,
  results,
  splitId,
  handleUserClicked,
  driverDisplayIdentifier,
}) {
  const splitResults = useMemo(() => {
    if (!results) return []
    if (!splitId) return results

    return results.filter((r) => r.split?.id === splitId)
  }, [splitId, results])

  const teamResults = useMemo(() => {
    const teamMap = new Map()

    for (const result of splitResults) {
      const teamId = result.team?.id

      if (!teamId) continue

      if (!teamMap.has(teamId)) {
        teamMap.set(teamId, {
          team: result.team,
          total: 0,
          members: new Set(),
          carClasses: new Map(),
          splits: new Map(),
        })
      }

      const teamData = teamMap.get(teamId)
      teamData.total += result.total
      teamData.members.add(result.user)

      if (result.carClass)
        teamData.carClasses.set(result.carClass.id, result.carClass)

      if (result.split) teamData.splits.set(result.split.id, result.split)
    }

    const sortedTeams = Array.from(teamMap.values())
    sortedTeams.forEach((teamData) => {
      teamData.members = Array.from(teamData.members)
      teamData.carClasses = Array.from(teamData.carClasses.values())
      teamData.splits = Array.from(teamData.splits.values())
    })

    return sortedTeams.sort((a, b) => b.total - a.total)
  }, [splitResults])

  const showSplits = useMemo(
    () =>
      championship.splits.length > 0 &&
      teamResults.some((result) => result.splits.length > 0),
    [championship.splits.length, teamResults],
  )

  const showCarClasses = useMemo(
    () =>
      championship.carClasses.length > 0 &&
      teamResults.some((result) => result.carClasses.length > 0),
    [championship.carClasses.length, teamResults],
  )

  return (
    <Table>
      <TableHeader>
        <TableRow isHeader>
          <TableHead className="hidden w-[68px] md:table-cell">Pos</TableHead>

          <TableHead className="hidden md:table-cell">Team</TableHead>

          <TableHead className="hidden md:table-cell">Drivers</TableHead>

          {showSplits && (
            <TableHead className="hidden md:table-cell">
              Tiers / Splits
            </TableHead>
          )}

          {showCarClasses && (
            <TableHead className="hidden min-w-[150px] md:table-cell">
              Car Classes
            </TableHead>
          )}

          <TableHead className="hidden w-[68px] md:table-cell">Pts</TableHead>
        </TableRow>
      </TableHeader>

      <TableBody className="overflow-x-auto">
        {isLoading ? (
          <TableRow isLoading>
            <TableCell colSpan={10}>
              <Loader size="lg" />
            </TableCell>
          </TableRow>
        ) : teamResults.length > 0 ? (
          teamResults.map((result, index) => (
            <StandingsTeamTableRow
              key={result.team.id}
              rowIndex={index}
              result={result}
              showSplits={showSplits}
              showCarClasses={showCarClasses}
              handleUserClicked={handleUserClicked}
              driverDisplayIdentifier={driverDisplayIdentifier}
            />
          ))
        ) : (
          <TableRow>
            <TableCell className="text-center" colSpan={10}>
              No results published.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
